/**
 * heuristicIntelligence — IntelligencePort adapter using deterministic
 * math on actor attributes + signals.
 *
 * This is the baseline. When a real ML model is available, add
 * `adapters/intelligence/ml.js` implementing the same port.
 */

// Fracción determinística en [0, 1) derivada de un seed de texto — reemplaza
// Math.random() en las dos ramas que lo usaban (windowDays y estimateValue de
// Recovery). Sin esto, la misma cuenta recibía un valor estimado distinto en
// cada recálculo (P1 ya documentado en SESSION_STATE.md, "quitar Math.random
// de estimaciones") — contradecía el propio docstring de este adapter
// ("deterministic math"). Usa el id del actor como seed: mismo actor, mismo
// resultado, siempre, hasta que un dato real (historicalRevenue, etc.) cambie.
function stableFraction(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return (hash >>> 0) / 4294967296;
}

/** @returns {import('../../domain/ports').IntelligencePort} */
export function createHeuristicIntelligence() {
  // Recovery: peor mientras más meses inactivo, con bonus por historial alto.
  const scoreRecovery = (actor) => {
    const monthsInactive = actor.verticalAttributes?.monthsInactive ?? 6;
    const historical = actor.historicalRevenue?.amount || 0;
    const base = Math.max(0, 100 - monthsInactive * 4);
    const bonus = Math.min(30, Math.floor(historical / 3000));
    return Math.min(100, Math.max(5, base + bonus));
  };

  // Renewal: más urgente mientras más cerca esté la fecha de renovación.
  // A diferencia de Recovery, "urgente" aquí significa "actuar pronto",
  // no "cliente en mal estado" — un cliente activo puede tener prioridad
  // alta simplemente porque su renovación es en 10 días.
  const scoreRenewal = (actor) => {
    const days = actor.verticalAttributes?.daysUntilRenewal;
    if (days == null) return 0;
    if (days <= 15) return 90;
    if (days <= 45) return 60;
    if (days <= 90) return 35;
    return 15;
  };

  // Cross-Sell: la señal ya viene como score 0-100 (crossSellPotential) —
  // no hay fecha ni inactividad que traducir, se usa tal cual.
  // Construido en bloque aislado (Sprint 11) — no se invoca desde ningún
  // flujo en vivo hasta que cross_sell se conecte en container.js.
  const scoreCrossSell = (actor) => {
    const potential = actor.verticalAttributes?.crossSellPotential;
    if (potential == null) return 0;
    return Math.min(100, Math.max(0, potential));
  };

  return {
    async scoreActor({ actor, engineId }) {
      const score =
        engineId === "renewal" ? scoreRenewal(actor)
        : engineId === "cross_sell" ? scoreCrossSell(actor)
        : scoreRecovery(actor);
      const priority = score >= 70 ? "high" : score >= 40 ? "medium" : "low";

      if (engineId === "renewal") {
        const days = Math.max(actor.verticalAttributes?.daysUntilRenewal ?? 30, 1);
        // Renovar a un cliente que sigue activo parte de una probabilidad
        // base más alta que reactivar a uno inactivo — es la misma relación,
        // no una nueva.
        const probability = Math.min(0.9, Math.max(0.4, score / 100 + 0.15));
        return { score, priority, probability, windowDays: days };
      }

      if (engineId === "cross_sell") {
        const probability = Math.min(0.85, Math.max(0.3, score / 100));
        const windowDays =
          priority === "high" ? 10 : priority === "medium" ? 21 : 35;
        return { score, priority, probability, windowDays };
      }

      const probability = Math.min(0.95, Math.max(0.15, score / 100));
      const jitter = stableFraction(`${actor.id}:window`);
      const windowDays =
        priority === "high" ? 6 + Math.floor(jitter * 5)
        : priority === "medium" ? 15 + Math.floor(jitter * 10)
        : 30 + Math.floor(jitter * 20);
      return { score, priority, probability, windowDays };
    },

    async estimateValue({ actor, probability }) {
      const monthly = actor.monthlyAverage?.amount || 0;
      const factor = 8 + Math.floor(stableFraction(`${actor.id}:value`) * 10);
      const amount = Math.round(monthly * factor * probability);
      return {
        label: "commercial_value_estimated",
        value: { amount, currency: "USD" },
        confidence: probability,
        sampleSize: 200,
        errorMarginPct: 11,
        rationale:
          "Facturación promedio histórica × factor de recuperación observado en tu vertical. " +
          "Se recalcula con cada outcome registrado.",
        lastRecalculatedAt: new Date().toISOString(),
      };
    },
  };
}
